import React, { useCallback, useMemo, useState } from 'react';
import { Platform, ScrollView, View } from 'react-native';
import { useRouter } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import MetaHead from '@/components/MetaHead';
import PublicFooter from '@/components/PublicFooter';
import { landingPalette } from '@/components/Landing/landingTokens';
import PricingClosing from '@/components/Pricing/PricingClosing';
import useLandingStyles from '@/components/Landing/useLandingStyles';
import PricingHero from '@/components/Pricing/PricingHero';
import PricingPlans from '@/components/Pricing/PricingPlans';
import PricingCredits from '@/components/Pricing/PricingCredits';
import PricingFaq from '@/components/Pricing/PricingFaq';
import { isDarkTheme } from '@/constants/Colors';
import { useTheme } from '@/context/ThemeContext';
import { useAuthStore, useModalStore } from '@/store';
import { useGabPurchase } from '@/context/GabPurchaseContext';
import { BILLING, FAQ } from '@/constants/pricingContent';
import { checkIsIapSubscriber } from '@/lib/purchaseUtils';

/** FAQ markup search engines can read, built from the same source as the page. */
const FAQ_SCHEMA = {
  '@type': 'FAQPage',
  mainEntity: FAQ.map((item) => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: { '@type': 'Answer', text: item.answer },
  })),
};

/**
 * /pricing, art-directed with the same kit as the landing page.
 *
 * The page argues in one direction: what it costs, what a credit is, what a
 * credit buys, and then the questions that stop people signing up.
 */
function PricingPageInner() {
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const [isAnnual, setIsAnnual] = useState(true);

  const { openModal } = useModalStore((state) => state.actions);
  const { openCheckout } = useGabPurchase();
  const token = useAuthStore((state) => state.token);
  const isTokenExpired = useAuthStore((state) => state.isTokenExpired);
  const isAuthenticated = Boolean(token) && !isTokenExpired();
  const isPlus = useAuthStore((state) => state.user?.flags?.isPlus);
  const isPlusPremium = useAuthStore((state) => state.user?.flags?.isPlusPremium);
  const activePurchase = useAuthStore((state) => state.user?.activePurchase);
  const isIapSubscriber = useMemo(
    () => checkIsIapSubscriber(activePurchase),
    [activePurchase],
  );

  /**
   * Signed-in visitors get the in-app purchase flow; everyone else goes to
   * signup, since there is nothing to upgrade yet.
   * Plus Premium is web-only — checkout opens directly with the premium plan.
   */
  const handleSelectPlan = useCallback((plan) => {
    if (plan.id === 'enterprise') {
      router.push('/enterprise');
      return;
    }
    if (!isAuthenticated) {
      router.push('/signup');
      return;
    }
    if (plan.id === 'plus') {
      if (isPlus || isPlusPremium) {
        openModal('SettingsModalWeb');
        return;
      }
      openModal('PricingModal');
      return;
    }
    if (plan.id === 'plus-premium') {
      if (isPlusPremium) {
        openModal('SettingsModalWeb');
        return;
      }
      if (isIapSubscriber) {
        // App Store / Play Plus cannot stack with web Premium.
        openModal('SettingsModalWeb');
        return;
      }
      if (Platform.OS !== 'web') {
        openModal('PricingModal');
        return;
      }
      const plusPlanId = isAnnual ? 'premium-yearly' : 'premium-monthly';
      openCheckout({ plusPlanId }).catch((err) => {
        console.error('Plus Premium checkout error:', err);
      });
      return;
    }
    router.push('/app');
  }, [isAnnual, isAuthenticated, isPlus, isPlusPremium, isIapSubscriber, openCheckout, openModal, router]);

  const handleBuyCredits = useCallback(() => {
    if (!isAuthenticated) router.push('/signup');
    else openModal('CreditsPurchaseModal');
  }, [isAuthenticated, openModal, router]);

  return (
    <>
      <PricingHero
        topInset={insets.top}
        isAnnual={isAnnual}
        onBillingChange={setIsAnnual}
      />
      <PricingPlans isAnnual={isAnnual} onSelectPlan={handleSelectPlan} />
      <PricingCredits onBuyCredits={handleBuyCredits} />
      <PricingFaq />
      <PricingClosing />
    </>
  );
}

export default function PricingPage() {
  const { theme } = useTheme();
  const isDark = isDarkTheme(theme);

  useLandingStyles();

  const containerStyle = useMemo(() => ({
    flex: 1,
    backgroundColor: landingPalette(isDark).page,
  }), [isDark]);

  return (
    <View style={containerStyle}>
      <MetaHead
        title="Pricing"
        description={`Gab AI Plus unlocks Work mode for agentic, multi-step tasks, plus every AI model, creative studios, and monthly credits. $${BILLING.annualPerMonth}/mo billed annually.`}
        canonical="/pricing"
        keywords="Gab AI pricing, Work mode AI, agentic AI, AI subscription cost, AI credits, AI image generator cost, AI video generator price, ChatGPT alternative price"
        structuredData={{
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'WebPage',
              name: 'Gab AI Pricing',
              description: 'Gab AI Plus unlocks Work mode for agentic, multi-step tasks, plus premium AI models, creative studios, and monthly credits.',
              url: 'https://gab.ai/pricing',
              mainEntity: {
                '@type': 'Product',
                name: 'Gab AI Plus',
                description: 'AI subscription with Work mode, every leading model, creative studios, monthly credits, and API access.',
                offers: {
                  '@type': 'Offer',
                  price: BILLING.annualPerMonth,
                  priceCurrency: 'USD',
                  availability: 'https://schema.org/InStock',
                  url: 'https://gab.ai/pricing',
                },
              },
            },
            FAQ_SCHEMA,
          ],
        }}
      />

      <ScrollView
        {...(Platform.OS === 'web' ? { dataSet: { landingScroll: 'true' } } : {})}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ alignItems: 'center' }}
        accessibilityRole="main"
        nativeID="main-content"
        style={{ position: 'relative', zIndex: 1 }}
      >
        <PricingPageInner />
        <PublicFooter variant="editorial" showHero={false} />
      </ScrollView>
    </View>
  );
}
